import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, MapPin, FileText, Award, Trash2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import PageHeader from '../../components/PageHeader';
import { apiGet, apiPut, apiDel, apiPost } from '../../lib/api';
import { computeQualificationVerdict, verdictChipClass } from '../../lib/qualification';
import DeadlineAlertsBanner from '../../components/crm/DeadlineAlertsBanner';
import TenderCalendar from '../../components/crm/TenderCalendar';

const VerdictChip = ({ enquiry, compact = false }) => {
  const { score, verdict } = computeQualificationVerdict(enquiry?.qualification);
  if (!verdict) return null;
  return (
    <span
      data-testid={`enq-verdict-${enquiry.id}`}
      title={`Decision Matrix score: ${score.toFixed(0)}/100 · ${verdict}`}
      className={`tag border whitespace-nowrap flex items-center gap-1 ${verdictChipClass(verdict)}`}
    >
      {compact ? verdict.replace('CONDITIONAL ', 'COND ') : verdict}
      <span className="font-mono tabular-nums opacity-90">{score.toFixed(0)}</span>
    </span>
  );
};

function AwardModal({ enq, onClose, onDone }) {
  const [revs, setRevs] = useState([]);
  const [form, setForm] = useState({
    awarded_revision_id: enq.awarded_revision_id || '',
    contract_type: 'Lumpsum Contract', contract_value: '',
    payment_terms: enq.payment_terms || '', loa_date: '', loa_ref: '', commencement_date: '',
    completion_date: '', retention_pct: enq.payment_retention_pct ?? '',
    defect_liability_period: '', execution_notes: '', radius_m: 500,
  });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    apiGet(`/enquiries/${enq.id}/revisions`).then((r) => {
      const items = r.items || [];
      setRevs(items);
      const awarded = items.find((x) => x.outcome === 'awarded') || items[0];
      if (awarded) {
        const val = awarded.quoted_amount_summary?.grand_total ?? awarded.estimated_value ?? '';
        setForm((s) => ({ ...s, awarded_revision_id: s.awarded_revision_id || awarded.id, contract_value: s.contract_value || val }));
      }
    }).catch(() => {});
  }, [enq.id]);
  const pickRev = (id) => {
    const r = revs.find((x) => x.id === id);
    const val = r ? (r.quoted_amount_summary?.grand_total ?? r.estimated_value ?? '') : '';
    setForm((s) => ({ ...s, awarded_revision_id: id, contract_value: val || s.contract_value }));
  };
  const submit = async () => {
    setSaving(true);
    try {
      const res = await apiPost(`/enquiries/${enq.id}/award`, {
        ...form,
        contract_value: form.contract_value === '' ? null : Number(form.contract_value),
        retention_pct: form.retention_pct === '' ? null : Number(form.retention_pct),
      });
      toast.success(`Project created · ID ${res.project_id?.slice(0, 8)}…`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not award'); }
    finally { setSaving(false); }
  };
  const L = 'block text-[10px] font-display font-bold uppercase tracking-wider text-slate-500';
  const I = 'mt-1 w-full border border-slate-300 rounded-md px-2.5 py-1.5 text-sm focus:ring-1 focus:ring-[#3b4872] outline-none';
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-md w-full max-w-2xl my-6" data-testid="award-modal">
        <div className="flex items-center justify-between border-b px-5 py-3">
          <h3 className="font-display font-bold text-sm">Award "{enq.project_name}" &rarr; Project</h3>
          <button onClick={onClose} data-testid="award-close" className="text-slate-400 hover:text-slate-700"><Award size={16} /></button>
        </div>
        <div className="p-5 grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className={L}>Awarded revision</label>
            <select className={I} value={form.awarded_revision_id} onChange={(e) => pickRev(e.target.value)} data-testid="award-revision">
              <option value="">&mdash; select &mdash;</option>
              {revs.map((r) => <option key={r.id} value={r.id}>{r.round_label || `R${r.revision_no}`}{r.effective_outcome ? ` · ${r.effective_outcome}` : ''}</option>)}
            </select>
          </div>
          <div><label className={L}>Contract value (₹)</label><input type="number" className={I} value={form.contract_value} onChange={(e) => setForm((s) => ({ ...s, contract_value: e.target.value }))} data-testid="award-contract-value" /></div>
          <div><label className={L}>Contract type</label><input className={I} value={form.contract_type} onChange={(e) => setForm((s) => ({ ...s, contract_type: e.target.value }))} data-testid="award-contract-type" /></div>
          <div className="col-span-2"><label className={L}>Payment terms</label><input className={I} value={form.payment_terms} onChange={(e) => setForm((s) => ({ ...s, payment_terms: e.target.value }))} data-testid="award-payment-terms" /></div>
          <div><label className={L}>LOA date</label><input type="date" className={I} value={form.loa_date} onChange={(e) => setForm((s) => ({ ...s, loa_date: e.target.value }))} data-testid="award-loa-date" /></div>
          <div><label className={L}>LOA reference</label><input className={I} value={form.loa_ref} onChange={(e) => setForm((s) => ({ ...s, loa_ref: e.target.value }))} placeholder="e.g. LOA/2026/045" data-testid="award-loa-ref" /></div>
          <div><label className={L}>Commencement</label><input type="date" className={I} value={form.commencement_date} onChange={(e) => setForm((s) => ({ ...s, commencement_date: e.target.value }))} data-testid="award-commencement" /></div>
          <div><label className={L}>Completion</label><input type="date" className={I} value={form.completion_date} onChange={(e) => setForm((s) => ({ ...s, completion_date: e.target.value }))} data-testid="award-completion" /></div>
          <div><label className={L}>Retention (%)</label><input type="number" className={I} value={form.retention_pct} onChange={(e) => setForm((s) => ({ ...s, retention_pct: e.target.value }))} data-testid="award-retention" /></div>
          <div className="col-span-2"><label className={L}>Defect liability period</label><input className={I} value={form.defect_liability_period} onChange={(e) => setForm((s) => ({ ...s, defect_liability_period: e.target.value }))} placeholder="e.g. 12 months" data-testid="award-dlp" /></div>
          <div className="col-span-2"><label className={L}>Notes for execution</label><textarea rows={2} className={I} value={form.execution_notes} onChange={(e) => setForm((s) => ({ ...s, execution_notes: e.target.value }))} data-testid="award-notes" /></div>
        </div>
        <div className="flex justify-end gap-2 border-t px-5 py-3">
          <button onClick={onClose} className="px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-100 rounded-md">Cancel</button>
          <button onClick={submit} disabled={saving} className="px-4 py-1.5 bg-emerald-600 text-white text-xs font-bold rounded-md hover:bg-emerald-700 disabled:opacity-50" data-testid="award-submit">{saving ? 'Awarding…' : 'Award & create project'}</button>
        </div>
      </div>
    </div>
  );
}


const RevBadge = ({ label, outcome, id }) => {
  if (!label) return <span className="text-[10px] text-slate-300" data-testid={`enq-rev-badge-${id}`}>—</span>;
  const outcomeLabel = outcome ? outcome.charAt(0).toUpperCase() + outcome.slice(1) : null;
  return (
    <span
      data-testid={`enq-rev-badge-${id}`}
      title={`Latest our-cost revision round: ${label}${outcomeLabel ? ` · ${outcomeLabel}` : ''}`}
      className="tag border bg-[#eef1f8] text-[#3b4872] border-[#c9d2ec] font-bold whitespace-nowrap"
    >
      {label}{outcomeLabel ? ` · ${outcomeLabel}` : ''}
    </span>
  );
};

const STAGE_COLORS = {
  'Lead Identified':           'bg-cyan-50 text-cyan-800 border-cyan-200',
  'Provisional Qualification': 'bg-blue-50 text-blue-800 border-blue-200',
  'Site Visit':                'bg-amber-50 text-amber-800 border-amber-200',
  'Costing and Documentation': 'bg-indigo-50 text-indigo-800 border-indigo-200',
  'Proposal Submitted':        'bg-violet-50 text-violet-800 border-violet-200',
  'Negotiation':               'bg-orange-50 text-orange-800 border-orange-200',
  'Won':                       'bg-emerald-50 text-emerald-800 border-emerald-200',
  'Lost':                      'bg-rose-50 text-rose-800 border-rose-200',
  'On Hold':                   'bg-slate-100 text-slate-700 border-slate-300',
  // Legacy aliases for existing data
  'New Enquiry':   'bg-cyan-50 text-cyan-800 border-cyan-200',
  'Qualification': 'bg-blue-50 text-blue-800 border-blue-200',
  'Costing':       'bg-indigo-50 text-indigo-800 border-indigo-200',
  'Proposal':      'bg-violet-50 text-violet-800 border-violet-200',
  'Awarded':       'bg-emerald-50 text-emerald-800 border-emerald-200',
};

export default function EnquiryTracker() {
  const navigate = useNavigate();
  const [enquiries, setEnquiries] = useState([]);
  const [stages, setStages] = useState([]);
  const [view, setView] = useState('list');
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);
  const [awardEnq, setAwardEnq] = useState(null);

  const load = async () => {
    try {
      const res = await apiGet('/enquiries');
      setEnquiries(res.items || []);
      // §8: add exit-stage columns so Awarded, Parked and On Hold appear in Kanban
      const base = res.stages || [];
      const exitStages = ['On Hold', 'Parked', 'Won', 'Awarded'];
      const extra = exitStages.filter(s => !base.includes(s));
      setStages([...base, ...extra]);
    } catch (e) { toast.error('Could not load enquiries'); }
  };

  useEffect(() => { load(); }, []);

  // Refresh when tab/window regains focus or becomes visible
  useEffect(() => {
    const onFocus = () => load();
    const onVis = () => { if (document.visibilityState === 'visible') load(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Open the rich 30+ field tender workspace for both new and existing enquiries
  const openWorkspace = (id) => navigate(`/app/estimation/enquiries/${id}`);
  const openCreate   = ()   => navigate('/app/estimation/enquiries/new');

  const moveCard = async (card, newStage) => {
    if (!card || card.stage === newStage) return;
    const prevStage = card.stage;
    setEnquiries((list) => list.map((x) => (x.id === card.id ? { ...x, stage: newStage } : x)));
    try {
      await apiPut(`/enquiries/${card.id}`, {
        client: card.client,
        project_name: card.project_name,
        stage: newStage,
      });
      toast.success(`Moved to ${newStage}`);
    } catch (err) {
      setEnquiries((list) => list.map((x) => (x.id === card.id ? { ...x, stage: prevStage } : x)));
      const data = err?.response?.data?.detail;
      const msg = (data && typeof data === 'object') ? data.message : data;
      toast.error(typeof msg === 'string' ? msg : 'Could not move card');
    }
  };

  const remove = async (enq, e) => {
    e?.stopPropagation();
    if (!window.confirm(`Remove enquiry "${enq.project_name}" from your view?\n\nThis will soft-delete it — a super-admin can restore it if needed.`)) return;
    try { await apiDel(`/enquiries/${enq.id}`); toast.success('Enquiry removed'); load(); }
    catch { toast.error('Could not remove'); }
  };

  const award = (enq, ev) => {
    ev?.stopPropagation();
    setAwardEnq(enq);
  };

  return (
    <div>
      {awardEnq && <AwardModal enq={awardEnq} onClose={() => setAwardEnq(null)} onDone={() => { setAwardEnq(null); load(); }} />}
      <PageHeader overline="Business Development" title="Enquiry Tracker"
        subtitle="Capture live enquiries → Lead Identified → Provisional Qualification → Site Visit → Costing and Documentation → Proposal Submitted → Negotiation → Won / Lost / On Hold." />

      <DeadlineAlertsBanner onPick={(id) => navigate(`/app/estimation/enquiries/${id}`)} />

      <div className="bg-white border border-slate-200 rounded-sm">
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-2.5">
          <div className="flex items-center gap-2">
            <button data-testid="enq-view-list" onClick={() => setView('list')} className={`px-3 py-1.5 text-xs font-display font-bold uppercase tracking-wider rounded-sm ${view === 'list' ? 'bg-[#3b4872] text-white hover:bg-[#2d3856]' : 'text-slate-600 hover:bg-slate-100'}`}>List</button>
            <button data-testid="enq-view-kanban" onClick={() => setView('kanban')} className={`px-3 py-1.5 text-xs font-display font-bold uppercase tracking-wider rounded-sm ${view === 'kanban' ? 'bg-[#3b4872] text-white hover:bg-[#2d3856]' : 'text-slate-600 hover:bg-slate-100'}`}>Kanban</button>
            <button data-testid="enq-view-calendar" onClick={() => setView('calendar')} className={`px-3 py-1.5 text-xs font-display font-bold uppercase tracking-wider rounded-sm ${view === 'calendar' ? 'bg-[#3b4872] text-white hover:bg-[#2d3856]' : 'text-slate-600 hover:bg-slate-100'}`}>Calendar</button>
            <span className="ml-2 text-[11px] uppercase tracking-wider text-slate-500 font-display">{enquiries.length} enquiries</span>
          </div>
          <button data-testid="enq-add-btn" onClick={openCreate} className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-[#3b4872] text-white rounded-sm hover:bg-[#2d3856] font-display font-bold uppercase tracking-wider">
            <Plus size={13} /> New Enquiry
          </button>
        </div>

        <div className="p-4">
          {view === 'calendar' ? (
            <TenderCalendar onPick={(id) => navigate(`/app/estimation/enquiries/${id}`)} />
          ) : view === 'list' ? (
            <table className="w-full dense-table" data-testid="enq-table">
              <thead>
                <tr>
                  <th>Enquiry No</th><th>Client</th><th>Project</th><th>Location</th>
                  <th className="text-right">Value (₹ Cr)</th><th>Stage</th><th className="text-center">Rev</th><th>Verdict</th><th>Approval</th><th>SVR</th><th></th>
                </tr>
              </thead>
              <tbody>
                {enquiries.length === 0 && (
                  <tr><td colSpan="10" className="text-center py-10 text-slate-400 text-xs uppercase tracking-widest font-display">No enquiries yet. Click <strong>New Enquiry</strong> to add the first one.</td></tr>
                )}
                {enquiries.map((e) => (
                  <tr key={e.id} data-testid={`enq-row-${e.id}`}
                    onClick={() => openWorkspace(e.id)}
                    className="cursor-pointer hover:bg-slate-50"
                  >
                    <td className="font-mono text-xs">
                      {e.enquiry_no}
                      {e.walk_in_tender && (
                        <span className="ml-1 text-[9px] bg-amber-100 text-amber-700 border border-amber-200 px-1 py-0.5 rounded font-bold" title="Walk-in tender">WALK-IN</span>
                      )}
                    </td>
                    <td className="font-display font-semibold">{e.client}</td>
                    <td>{e.project_name}</td>
                    <td className="text-xs">{e.location || '—'}</td>
                    <td className="text-right tabular-nums font-display font-bold">{e.estimated_value_cr ? `₹${Number(e.estimated_value_cr).toFixed(2)} Cr` : '—'}</td>
                    <td>
                      <span
                        data-testid={`enq-stage-badge-${e.id}`}
                        title="Stage changes only via the enquiry's Save & Move or the GO/NO-GO decisions"
                        className={`tag border ${STAGE_COLORS[e.stage] || 'bg-slate-50 text-slate-700 border-slate-200'}`}
                      >
                        {e.stage}
                      </span>
                    </td>
                    <td className="text-center"><RevBadge label={e.latest_round_label} outcome={e.latest_round_outcome} id={e.id} /></td>
                    <td><VerdictChip enquiry={e} compact /></td>
                    <td>
                      <div className="flex flex-col gap-0.5 items-start" data-testid={`enq-approval-chip-${e.id}`}>
                        {(e.management_approval || e.provisional_decision) && (
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-sm border ${
                            (e.management_approval || e.provisional_decision) === 'GO' ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                              : 'bg-rose-50 text-rose-700 border-rose-200'
                          }`}>P: {e.management_approval || e.provisional_decision}</span>
                        )}
                        {e.final_decision && (
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-sm border ${
                            e.final_decision === 'GO' ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                              : 'bg-rose-50 text-rose-700 border-rose-200'
                          }`}>F: {e.final_decision}</span>
                        )}
                        {!(e.management_approval || e.provisional_decision || e.final_decision) && e.stage === 'Provisional Qualification' && (
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-sm border bg-amber-50 text-amber-700 border-amber-200">Approval Pending</span>
                        )}
                      </div>
                    </td>
                    <td>
                      {e.site_visit_report ? (
                        <button data-testid={`enq-svr-${e.id}`}
                          onClick={(ev) => { ev.stopPropagation(); navigate(`/app/estimation/enquiries/${e.id}/svr`); }}
                          className="text-xs font-display font-bold tracking-wider uppercase flex items-center gap-1 text-emerald-700">
                          <FileText size={12} /> View SVR
                        </button>
                      ) : (e.site_visit_decision === 'NO' || e.site_visit_waived) ? (
                        <span data-testid={`enq-svr-${e.id}`} className="text-xs font-display font-bold tracking-wider uppercase text-slate-500" title="Site visit waived — tender prepared without a visit">
                          Not required
                        </span>
                      ) : (
                        <button data-testid={`enq-svr-${e.id}`}
                          onClick={(ev) => { ev.stopPropagation(); navigate(`/app/estimation/enquiries/${e.id}/svr`); }}
                          className="text-xs font-display font-bold tracking-wider uppercase flex items-center gap-1 text-slate-400 hover:text-[#3b4872]">
                          <FileText size={12} /> Add SVR
                        </button>
                      )}
                    </td>
                    <td className="text-right">
                      <div className="flex items-center gap-2 justify-end">
                        <button
                          data-testid={`enq-workspace-${e.id}`}
                          onClick={(ev) => { ev.stopPropagation(); openWorkspace(e.id); }}
                          title="Tender Workspace · Documents · AI Auto-Fill"
                          className="text-violet-600 hover:text-violet-800"
                        >
                          <Sparkles size={13} />
                        </button>
                        {e.stage !== 'Awarded' && e.stage !== 'Lost' && (
                          <button data-testid={`enq-award-${e.id}`}
                            onClick={(ev) => award(e, ev)}
                            title="Award & Convert to Project"
                            className="text-emerald-600 hover:text-emerald-800"
                          >
                            <Award size={13} />
                          </button>
                        )}
                        <button data-testid={`enq-del-${e.id}`}
                          onClick={(ev) => remove(e, ev)}
                          className="text-rose-500 hover:text-rose-700"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="flex gap-3 overflow-x-auto pb-3" data-testid="enq-kanban">
              {stages.map((s) => {
                const cards = enquiries.filter((x) => x.stage === s);
                return (
                  <div
                    key={s}
                    className={`min-w-[260px] flex-1 bg-slate-50 rounded-sm border p-2 transition-colors ${dragOverStage === s ? 'border-[#3b4872] ring-2 ring-[#3b4872]/40 bg-slate-100' : 'border-slate-200'}`}
                    data-testid={`enq-col-${s}`}
                    onDragOver={(e) => { if (draggingId) { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (dragOverStage !== s) setDragOverStage(s); } }}
                    onDragLeave={(e) => { if (e.currentTarget === e.target && dragOverStage === s) setDragOverStage(null); }}
                    onDrop={(e) => {
                      e.preventDefault();
                      const id = draggingId || e.dataTransfer.getData('text/plain');
                      const card = enquiries.find((x) => x.id === id);
                      moveCard(card, s);
                      setDraggingId(null);
                      setDragOverStage(null);
                    }}
                  >
                    <div className="flex items-center justify-between mb-2 px-1">
                      <span className={`text-[10px] font-display font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm border ${STAGE_COLORS[s] || 'bg-white text-slate-700 border-slate-300'}`}>{s}</span>
                      <span className="text-[10px] text-slate-500 font-display font-bold">{cards.length}</span>
                    </div>
                    <div className="space-y-2">
                      {cards.map((c) => (
                        <div
                          key={c.id}
                          draggable
                          onDragStart={(e) => { setDraggingId(c.id); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', c.id); }}
                          onDragEnd={() => { setDraggingId(null); setDragOverStage(null); }}
                          className={`bg-white border border-slate-200 rounded-sm p-2.5 hover:shadow-sm cursor-pointer ${draggingId === c.id ? 'opacity-40' : ''}`}
                          onClick={() =>
                            c.stage === 'Site Visit'
                              ? navigate(`/app/estimation/enquiries/${c.id}/svr`)
                              : openWorkspace(c.id)
                          }
                          data-testid={`enq-card-${c.id}`}
                        >
                          <div className="font-display font-bold text-xs truncate">{c.project_name}</div>
                          <div className="text-[11px] text-slate-600 truncate">{c.client}</div>
                          {c.location && <div className="text-[10px] text-slate-500 flex items-center gap-1 mt-1"><MapPin size={9} /> {c.location}</div>}
                          {c.estimated_value_cr ? <div className="text-[10px] text-slate-500 mt-1">₹{Number(c.estimated_value_cr).toFixed(2)} Cr</div> : null}
                          <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                            <VerdictChip enquiry={c} compact />
                            <RevBadge label={c.latest_round_label} outcome={c.latest_round_outcome} id={c.id} />
                            {c.site_visit_report ? <span className="text-[9px] text-emerald-700 font-bold uppercase tracking-wider">SVR ✓</span> : null}
                            {c.stage === 'Provisional Qualification' && (
                              <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-sm border ${
                                c.management_approval === 'GO' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                                c.management_approval === 'NO-GO' ? 'bg-rose-50 text-rose-700 border-rose-200' :
                                'bg-amber-50 text-amber-700 border-amber-200'
                              }`} data-testid={`enq-kanban-approval-${c.id}`}>
                                {c.management_approval === 'GO' ? 'GO ✓' : c.management_approval === 'NO-GO' ? 'NO-GO ✗' : 'Pending'}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center justify-between mt-1.5">
                            <span className="text-[9px] text-slate-400">
                              {c.created_at ? `${Math.floor((Date.now() - new Date(c.created_at)) / 86400000)}d` : ''}
                            </span>
                            <button
                              data-testid={`enq-card-workspace-${c.id}`}
                            onClick={(ev) => {
                              ev.stopPropagation();
                              c.stage === 'Site Visit'
                                ? navigate(`/app/estimation/enquiries/${c.id}/svr`)
                                : openWorkspace(c.id);
                            }}
                              title="Open Workspace"
                              className="text-violet-500 hover:text-violet-700"
                            ><Sparkles size={10} /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
